import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Flame, Droplets, Beaker, Zap, Bomb, Snowflake } from 'lucide-react';
import { Button } from '../ui/button';

const MechanismAssessment = ({ onNext, onBack, onChemical, onElectrical, onFrostbite }) => {
  const [selectedMechanisms, setSelectedMechanisms] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState('');

  const mechanisms = [
    { id: 'fire', title: 'Open Fire', icon: <Flame className="w-7 h-7" />, description: 'Assess for inhalation injury, especially in enclosed spaces' },
    { id: 'scald', title: 'Hot Liquid (Scald)', icon: <Droplets className="w-7 h-7" />, description: 'Note liquid type and temperature if known' },
    { id: 'frostbite', title: 'Frostbite', icon: <Snowflake className="w-7 h-7" />, description: 'Cold-induced tissue injury; assess depth, rewarm properly, prevent refreezing', hasSubPath: true },
    { id: 'chemical', title: 'Chemical', icon: <Beaker className="w-7 h-7" />, description: 'Identify agent. Irrigate copiously with water', hasSubPath: true },
    { id: 'explosion', title: 'Explosion', icon: <Bomb className="w-7 h-7" />, description: 'Assess for associated trauma from blast wave' },
    { id: 'electrical', title: 'Electrical', icon: <Zap className="w-7 h-7" />, description: 'Identify entry/exit points. Monitor cardiac rhythm', hasSubPath: true }
  ];

  const handleToggleMechanism = (mechanismId) => {
    setSelectedMechanisms([mechanismId]);
  };

  const handleViewDetails = () => {
    if (selectedMechanisms.includes('frostbite')) {
      onFrostbite();
    } else if (selectedMechanisms.includes('chemical')) {
      onChemical();
    } else if (selectedMechanisms.includes('electrical')) {
      onElectrical();
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-black py-4">
      <div className="w-full max-w-sm mx-auto px-4">
        <div className="flex flex-col min-h-screen bg-zinc-900 rounded-3xl border-2 border-orange-500/30 shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b-2 border-orange-500/30">
            <Button onClick={onBack} variant="ghost" className="flex items-center gap-2 text-orange-500 text-lg font-bold">
              <ArrowLeft className="w-6 h-6" />
              Back
            </Button>
            <h1 className="text-lg font-black text-orange-500">Mechanism</h1>
            <div className="w-16" />
          </div>

          <main className="flex-grow p-4 space-y-4 bg-black overflow-y-auto">
            <h2 className="text-xl font-black text-orange-400">How was the burn sustained?</h2>
            
            <div className="space-y-3">
              {mechanisms.map((mechanism) => (
                <div key={mechanism.id} className="bg-zinc-900 p-4 rounded-xl border-2 border-orange-500/20">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedMechanisms.includes(mechanism.id)}
                      onChange={() => handleToggleMechanism(mechanism.id)}
                      className="h-7 w-7 rounded border-2 border-orange-500 bg-transparent text-orange-500"
                    />
                    <div className="text-orange-500">{mechanism.icon}</div>
                    <span className="font-bold text-lg text-orange-100">{mechanism.title}</span>
                  </label>
                  <p className="mt-2 ml-10 text-base text-orange-200 leading-snug">{mechanism.description}</p>
                  {mechanism.hasSubPath && selectedMechanisms.includes(mechanism.id) && (
                    <Button onClick={handleViewDetails} variant="outline" className="mt-3 ml-10 text-base border-2 border-orange-500/50 text-orange-100 hover:bg-orange-500/20 font-bold py-2 rounded-xl">
                      View {mechanism.title} Protocol
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <textarea
              placeholder="Additional notes..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              className="w-full rounded-xl bg-zinc-900 border-2 border-orange-500/30 text-lg text-orange-100 placeholder-zinc-500 focus:ring-orange-500 focus:border-orange-500 min-h-[100px] p-4"
            />
          </main>

          <div className="p-4 border-t-2 border-orange-500/30 bg-zinc-900">
            <Button onClick={onNext} className="w-full h-14 bg-orange-500 text-white text-lg font-black rounded-xl hover:bg-orange-600 shadow-xl">
              Continue <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MechanismAssessment;